import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { sbomApi, itemUrl } from './sbomApi';
import useStickyHead from './useStickyHead';
import { Support } from './SbomReleases';
import SbomWhereUsedModal from './SbomWhereUsedModal';
import SbomComponentPage from './SbomComponentPage';
import { AssessDrawer, VulnChips } from './SbomAssess';

const PAGE = 100;

/**
 * The component catalog BY PACKAGE: one row per package with how many versions it has and in how
 * many releases; open it for each version (newest first) and the releases that ship it; View opens one
 * package's page (SbomComponentPage). A new
 * version is still a new Orcanos item, so older releases keep pointing at what they shipped.
 */
export default function SbomComponents({ info, onAuth }) {
  const headBox = useStickyHead();
  const s = info.settings;
  const [params, setParams] = useSearchParams();
  const where = params.get('where') || '';
  const opened = params.get('component') || '';
  const locateKey = params.get('locate') || '';   // the SBOM link field on an Orcanos component
  const [located, setLocated] = useState(null);    // {item_id, package} — highlighted until clicked
  const locRow = useRef(null);
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(() => new Set());
  const [assess, setAssess] = useState(null);
  const [tick, setTick] = useState(0);
  const vulnOn = s.osvEnabled !== false;
  const cols = vulnOn ? 8 : 7;

  useEffect(() => {
    let off = false;
    const t = setTimeout(() => {
      sbomApi.packages(q.trim(), page)
        .then((d) => { if (!off) { setData(d); setError(''); } })
        .catch((e) => { if (!off && !onAuth(e)) setError(e.message); });
    }, 250);
    return () => { off = true; clearTimeout(t); };
  }, [q, page, tick, onAuth]);

  // `?locate=<match key>`: find the component, search its package, open it and highlight the version.
  useEffect(() => {
    if (!locateKey) return;
    let off = false;
    sbomApi.locate(locateKey)
      .then((d) => {
        if (off) return;
        setLocated({ item_id: d.item_id, package: d.package });
        setOpen(new Set([d.package]));
        setQ(d.name || d.version || '');
        setPage(1);
      })
      .catch((e) => { if (!off && !onAuth(e)) setError(e.message); })
      .finally(() => { if (!off) setParams({}, { replace: true }); });
    return () => { off = true; };
  }, [locateKey, onAuth, setParams]);
  useEffect(() => {
    if (located && data && locRow.current) locRow.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [located, data]);

  const link = (iid) => itemUrl(info.baseUrl, s.versionId, s.componentType, iid);
  const relLink = (iid) => itemUrl(info.baseUrl, s.versionId, s.releaseType, iid);
  const pages = data ? Math.max(1, Math.ceil(data.total / PAGE)) : 1;
  const toggle = (pk) => setOpen((o) => { const n = new Set(o); if (n.has(pk)) n.delete(pk); else n.add(pk); return n; });
  // A search opens every match — you searched for it to see its versions.
  const isOpen = (pk) => open.has(pk) || (q.trim() !== '' && data && data.items.length <= 10);

  if (opened) {
    return <SbomComponentPage itemId={opened} info={info} onAuth={onAuth} onBack={() => setParams({})}
      onOpen={(iid) => setParams({ component: iid })} />;
  }

  return (
    <>
      <div className="sbom-head">
        <div>
          <h2>Software components</h2>
          <div className="sbom-sub">Every package once, with its versions. Open a package to see which version each
            release ships; <b>View</b> opens everything about one package (fields, releases, vulnerabilities, risks, PDF);
            <b>Where used</b> shows the full path — the first question after a new vulnerability is published.</div>
        </div>
      </div>
      {error && <div className="sbom-error" role="alert">{error}</div>}
      <div className="sbom-table-card">
        <div className="sbom-tbar">
          <input type="search" className="sbom-search" placeholder="Find a package or version" value={q} aria-label="Search packages"
            onChange={(e) => { setQ(e.target.value); setPage(1); setLocated(null); }} />
          <span style={{ flex: 1 }} />
          {data && <span className="sbom-tiny sbom-muted">{data.total} package{data.total === 1 ? '' : 's'}</span>}
        </div>
        <div className="sbom-tw sbom-tw--sticky" ref={headBox}>
          <table className="sbom-table">
            <thead>
              <tr><th>Package</th><th>Version</th><th>Key</th><th className="sbom-hide-s">Licence</th>
                <th className="sbom-hide-s">Support</th><th>Used in</th>{vulnOn && <th>Vulnerabilities</th>}<th /></tr>
            </thead>
            <tbody>
              {!data && <tr><td colSpan={cols} className="sbom-empty">Loading…</td></tr>}
              {data && data.items.length === 0 && (
                <tr><td colSpan={cols} className="sbom-empty">{q ? 'No package matches.' : 'Nothing imported yet.'}</td></tr>
              )}
              {data && data.items.map((p) => {
                const shown = isOpen(p.package);
                return (
                  <React.Fragment key={p.package}>
                    <tr className="sbom-pkg">
                      <td>
                        <div className="sbom-tree">
                          <button type="button" className="sbom-chev" aria-expanded={shown}
                            aria-label={shown ? `Collapse ${p.name}` : `Expand ${p.name}`} onClick={() => toggle(p.package)}>{shown ? '▾' : '▸'}</button>
                          <button type="button" className="sbom-link" title="Open the component page"
                            onClick={() => setParams({ component: p.versions[0].item_id })}><b>{p.name}</b></button>
                          {p.ecosystem && <span className="sbom-tiny sbom-muted">{p.ecosystem}</span>}
                        </div>
                      </td>
                      <td>
                        <span className={`sbom-pill ${p.version_count > 1 ? 'sbom-pill--acc' : 'sbom-pill--plain'}`}>
                          {p.version_count} version{p.version_count === 1 ? '' : 's'}
                        </span>
                      </td>
                      <td colSpan={3} className="sbom-hide-s" />
                      <td>
                        <span className="sbom-pill sbom-pill--plain">{p.release_count} release{p.release_count === 1 ? '' : 's'}</span>
                        {p.unused > 0 && <span className="sbom-tiny sbom-muted" title="Versions no release uses any more"> · {p.unused} unused</span>}
                      </td>
                      {vulnOn && <td>{p.vuln_count > 0 && <span className="sbom-pill sbom-pill--bad">{p.vuln_count} known</span>}</td>}
                      <td />
                    </tr>
                    {shown && p.versions.map((c) => (
                      <tr key={c.item_id} ref={located?.item_id === c.item_id ? locRow : undefined}
                        className={located?.item_id === c.item_id ? 'sbom-row--located' : (where === c.item_id ? 'sbom-sel' : undefined)}
                        onClick={located?.item_id === c.item_id ? () => setLocated(null) : undefined}
                        title={located?.item_id === c.item_id ? 'Click to clear the highlight' : undefined}>
                        <td><div className="sbom-tree"><span className="sbom-rail" aria-hidden="true" /></div></td>
                        <td className="sbom-mono">{c.version || '—'}</td>
                        <td>{c.key ? <a className="sbom-key" href={link(c.item_id)} target="_blank" rel="noreferrer">{c.key}</a> : '—'}</td>
                        <td className="sbom-hide-s sbom-mono">{c.licence || '—'}</td>
                        <td className="sbom-hide-s"><Support r={c} /></td>
                        <td className="sbom-rels">
                          {c.releases.length === 0 && <span className="sbom-muted">No release</span>}
                          {c.releases.map((r) => (
                            <a key={r.item_id} className="sbom-pill sbom-pill--info sbom-rel-pill" href={relLink(r.item_id) || undefined}
                              target="_blank" rel="noreferrer" title={r.key || r.name}>{r.name || r.key}</a>
                          ))}
                        </td>
                        {vulnOn && <td><VulnChips vulns={c.vulns} checkedAt={c.checked_at} checkable={c.checkable} onOpen={(v) => setAssess(v)} /></td>}
                        <td className="sbom-icon-acts">
                          <button type="button" className="sbom-act" onClick={() => setParams({ component: c.item_id })}>View</button>
                          <button type="button" className="sbom-act" onClick={() => setParams({ where: c.item_id })}>Where used</button>
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        {pages > 1 && (
          <div className="sbom-tbar" style={{ borderTop: '1px solid var(--orc-line)', borderBottom: 0 }}>
            <button type="button" className="sbom-btn" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</button>
            <span className="sbom-tiny sbom-muted">Page {page} of {pages}</span>
            <button type="button" className="sbom-btn" disabled={page >= pages} onClick={() => setPage(page + 1)}>Next</button>
          </div>
        )}
      </div>
      {assess && (
        <AssessDrawer vulnId={assess.id} pkg={assess.package_key} info={info} onAuth={onAuth}
          onClose={() => setAssess(null)} onSaved={() => setTick((t) => t + 1)} />
      )}
      {where && <SbomWhereUsedModal itemId={where} info={info} onClose={() => setParams({})} onAuth={onAuth} />}
    </>
  );
}
